import React, { createContext, useCallback, useEffect, useState } from "react";
import {
	Document,
	DocumentInBasket,
	GetItemsRequestBody,
	UserFeedback,
} from "../../types";
import { useConfig } from "../ConfigContext";
import { FilterType } from "../ConfigContext/types";
import { getDocumentById, getDocuments } from "../../services/fetch";
import { createUserFeedback } from "../../services/post";
import { deleteAllFeedbacks } from "../../services/delete";
import styles from "../../pages/Home/home.module.css";
import { useClientAccessContext } from "../ClientAccessContext";
import { useLocation, useNavigate } from "react-router-dom";
import { removeQueryParams, updateQueryParams } from "../../utils";

export interface IDocumentsProvider {
	documents: Document[];
	page: number;
	fetchingDocuments: boolean;
	totalPages: number;
	loading: boolean;
	documentsInBasket: DocumentInBasket[];
	setPage: React.Dispatch<React.SetStateAction<number>>;
	setDocumentsInBasket: React.Dispatch<
		React.SetStateAction<DocumentInBasket[]>
	>;
	handleUserFeedback: (userFeedback: UserFeedback) => void;
	handleUserFeedbackDelete: (labelId: number, documentId: number) => void;
	handleApplyFilters: () => void;
	addDocumentToBasket: (documentId: number) => void;
	removeDocumentFromBasket: (documentId: number) => void;
}

interface DocumentsContextProps {
	children?: React.ReactNode;
}

const DocumentsContext = createContext({} as IDocumentsProvider);

const DocumentsProvider: React.FunctionComponent<DocumentsContextProps> = ({
	children,
}) => {
	const { selectedClient } = useClientAccessContext();
	const {
		selectedTopic,
		user,
		sortOrder,
		startDate,
		endDate,
		filters: allFilters,
		searchQuery,
		usersTopics,
		queryParams,
		setSelectedTopic,
		setQueryParams,
		getCheckedItems,
	} = useConfig();
	const storageKey = `basket_${selectedClient?.clientName}_${selectedTopic?.name}`;
	const [documents, setDocuments] = useState<Document[]>([]);
	const [loading, setLoading] = useState(true);
	const [fetchingDocuments, setFetchingDocuments] = useState(false);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(0);
	const [documentsInBasket, setDocumentsInBasket] = useState<
		DocumentInBasket[]
	>([]);

	const scrollToTop = useCallback(() => {
		const elements = document.getElementsByClassName(styles.grid_container);
		if (!elements || !elements.length) return;
		elements[0].scrollTo(0, 0);
	}, []);

	const fetchDocuments = async (currentPage = page) => {
		if (!selectedTopic?.id) return;
		setQueryParams({});
		removeQueryParams("document_id");
		setLoading(true);
		setFetchingDocuments(true);
		const requestBody: GetItemsRequestBody = {
			TopicId: selectedTopic.id,
			PageNumber: currentPage,
			PageSize: 24,
			StartDate: startDate.toISOString(),
			EndDate: endDate?.toISOString(),
			SortOrder: sortOrder.order,
			OrderProperty: sortOrder.property,
			Categories: (getCheckedItems(FilterType.CATEGORY) as number[]) || [],
			Languages: (getCheckedItems(FilterType.LANGUAGE) as string[]) || [],
			Checked: getCheckedItems(FilterType.CHECKED) as boolean,
			includeText: searchQuery.includeText,
			excludeText: searchQuery.excludeText,
			includeDocuments: searchQuery.includeDocuments?.map((d) => d.id) || [],
			excludeDocuments: searchQuery.excludeDocuments?.map((d) => d.id) || [],
			clientName: selectedClient.clientName,
		};
		const fetchResponse = await getDocuments(requestBody);
		const { result: fetchResult, totalPages } = fetchResponse;

		setTotalPages(totalPages);
		setDocuments(fetchResult.length ? fetchResult : []);
		setLoading(false);
		setFetchingDocuments(false);
		scrollToTop();
	};

	useEffect(() => {
		if (!selectedClient?.clientName || !selectedTopic?.name) return;
		const json = window.localStorage.getItem(storageKey);
		setDocumentsInBasket(json ? JSON.parse(json) : []);
	}, [storageKey, selectedClient?.clientName, selectedTopic?.name]);

	useEffect(() => {
		if (!selectedClient?.clientName || !selectedTopic?.name) return;
		window.localStorage.setItem(storageKey, JSON.stringify(documentsInBasket));
	}, [
		documentsInBasket,
		storageKey,
		selectedClient?.clientName,
		selectedTopic?.name,
	]);

	useEffect(() => {
		if (!selectedTopic?.id) return setDocuments([]);
		if (!allFilters) return;

		const docIdParam = queryParams["document_id"];
		if (docIdParam) {
			fetchDocumentById(Number(docIdParam));
			updateQueryParams("document_id", docIdParam);
			return;
		}

		fetchDocuments(1);
	}, [selectedTopic, allFilters?.length]);

	useEffect(() => {
		setPage(1);
		fetchDocuments(1); // need to pass pageNumber here because the setPage is set async and does not get updated before the call
	}, [sortOrder]);

	useEffect(() => {
		fetchDocuments();
	}, [page]);

	const fetchDocumentById = async (documentId: number) => {
		if (!selectedClient) return;
		setLoading(true);
		try {
			const response = await getDocumentById(documentId);
			const document = response.result;
			if (document.topicId !== selectedTopic.id) {
				const topicToSet = usersTopics.find((t) => t.id === document.topicId);
				setSelectedTopic(topicToSet);
				return;
			}
			setDocuments([document]);
		} catch (error) {
			console.error("Error while fetching document id:", documentId);
			setDocuments([]);
		}
		setLoading(false);
	};

	const handleUserFeedback = (userFeedback: UserFeedback) => {
		userFeedback.userId = user.id;
		setDocuments((documents) => {
			return documents.map((d) => {
				if (d.id === userFeedback.documentId) {
					return {
						...d,
						labels: d.labels.map((label) => {
							if (label.labelName === userFeedback.labelName) {
								return { ...label, userFeedback };
							}
							return label;
						}),
					};
				}
				return d;
			});
		});

		createUserFeedback(userFeedback);
	};

	const handleUserFeedbackDelete = (labelId: number, documentId: number) => {
		setDocuments((documents) => {
			return documents.map((d) => {
				if (d.id === documentId) {
					return {
						...d,
						labels: d.labels.map((label) => {
							if (label.labelId === labelId) {
								return { ...label, userFeedback: { documentId: documentId } };
							}
							return label;
						}),
					};
				}
				return d;
			});
		});

		deleteAllFeedbacks({ labelId: labelId, documentId: documentId });
	};

	const handleApplyFilters = () => {
		setPage(1);
		fetchDocuments(1);
	};

	const addDocumentToBasket = (documentId: number) => {
		setDocumentsInBasket((prev) => {
			if (prev.some((d) => d.id === documentId)) {
				return prev.filter((d) => d.id !== documentId);
			}
			const doc = documents.find((d) => d.id === documentId);
			if (!doc) return prev;
			return [...prev, { id: doc.id, title: doc.title }];
		});
	};

	const removeDocumentFromBasket = (documentId: number) => {
		setDocumentsInBasket((prev) => prev.filter((d) => d.id !== documentId));
	};

	const documentContextValue = {
		documents,
		page,
		fetchingDocuments,
		totalPages,
		loading,
		documentsInBasket,
		setPage,
		setDocumentsInBasket,
		handleUserFeedback,
		handleUserFeedbackDelete,
		handleApplyFilters,
		addDocumentToBasket,
		removeDocumentFromBasket,
	};

	return (
		<DocumentsContext.Provider value={documentContextValue}>
			{children}
		</DocumentsContext.Provider>
	);
};

const useDocuments = () => {
	const context = React.useContext(DocumentsContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within a AuthProvider");
	}
	return context;
};

export { DocumentsProvider, useDocuments };
